import { showToast } from "./common";

async function fetchWithServerErrorToast(...args) {
  const response = await window.fetch(...args);
  if (response.status >= 500 && response.status < 600) {
    showToast(`Błąd serwera (HTTP ${response.status}). Jeśli problem się powtarza, zgłoś go administratorowi.`, {
      type: "danger",
      toastId: "http-server-error",
    });
  }
  return response;
}

/**
 * Central API service for all JSON HTTP requests.
 *
 * - Automatically forwards `preview_token` from the current page URL to every
 *   request so admin school previews work correctly even when API URLs in
 *   data-* attributes were not generated through get_permalink().
 * - Sets consistent headers: Accept, Content-Type, X-Requested-With.
 * - POSTs always include X-CSRF-Token.
 * - Throws on network errors and on responses where ok !== true.
 */

/**
 * Read the preview_token query param from the current page URL, if present.
 *
 * @returns {string}
 */
function getPreviewToken() {
  try {
    return new URLSearchParams(window.location.search).get("preview_token") || "";
  } catch {
    return "";
  }
}

/**
 * Append ?preview_token=... to a URL if the current page carries one and the
 * target URL does not already include it.
 *
 * @param {string} url
 * @returns {string}
 */
function withPreviewToken(url) {
  const token = getPreviewToken();
  if (!token) {
    return url;
  }

  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.searchParams.has("preview_token")) {
      return url;
    }

    parsed.searchParams.set("preview_token", token);
    return parsed.toString();
  } catch {
    // Malformed URL - return as-is.
    return url;
  }
}

/**
 * Make a GET request expecting a JSON response.
 * Throws on network error or when response.ok is false.
 *
 * @param {string} url
 * @returns {Promise<object>}
 */
async function getJson(url) {
  const response = await fetchWithServerErrorToast(withPreviewToken(url), {
    method: "GET",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(payload?.error || payload?.message || `Błąd HTTP ${response.status}.`);
  }

  return payload;
}

/**
 * Make a POST request with a JSON body and CSRF token.
 * Throws on network error or when the response payload has ok !== true.
 *
 * @param {string} url
 * @param {object} body
 * @param {string} csrfToken
 * @returns {Promise<object>}
 */
async function postJson(url, body = {}, csrfToken = "") {
  const response = await fetchWithServerErrorToast(withPreviewToken(url), {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken || "",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify(body),
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.toast?.message || payload?.message || "Nie udało się wykonać operacji.");
  }

  return payload;
}

/**
 * Make a POST request with FormData payload and optional CSRF token.
 * Throws on network error or when the response payload has ok !== true.
 *
 * @param {string} url
 * @param {FormData} body
 * @param {string} csrfToken
 * @returns {Promise<object>}
 */
async function postFormData(url, body, csrfToken = "") {
  const response = await fetchWithServerErrorToast(withPreviewToken(url), {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken || "",
      "X-Requested-With": "XMLHttpRequest",
    },
    body,
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.toast?.message || payload?.message || "Nie udało się wykonać operacji.");
  }

  return payload;
}

// NDJSON delivers progress while the server keeps one transaction open for the final save.
async function postFormDataProgress(url, body, csrfToken, onProgress) {
  const response = await fetchWithServerErrorToast(withPreviewToken(url), {
    method: "POST", credentials: "same-origin", body,
    headers: { Accept: "application/x-ndjson", "X-CSRF-Token": csrfToken, "X-Requested-With": "XMLHttpRequest" },
  });
  if (!response.ok || !response.headers.get("content-type")?.includes("application/x-ndjson")) {
    const payload = await response.json().catch(() => null);
    throw Object.assign(new Error(payload?.message || `Błąd HTTP ${response.status}.`), {
      operationRolledBack: payload?.ok === false && [400, 403, 422].includes(response.status),
    });
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result = null;
  function consume(line) {
    if (!line.trim()) return;
    const event = JSON.parse(line);
    if (event.type === "error") throw Object.assign(new Error(event.message), { operationRolledBack: event.rolledBack === true });
    if (event.type === "progress") onProgress(event);
    if (event.type === "complete" && event.ok === true) result = event;
  }
  try {
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      const lines = buffer.split("\n");
      buffer = lines.pop();
      lines.forEach(consume);
      if (done) break;
    }
    if (buffer.trim()) consume(buffer);
  } finally { reader.releaseLock(); }
  if (!result) throw new Error("Połączenie zakończyło się bez potwierdzenia zapisu.");
  return result;
}

export { getJson, postJson, postFormData, postFormDataProgress, withPreviewToken, fetchWithServerErrorToast };
